import { getClientPosition } from '@tableau/widgets';
import * as React from 'react';
import { PreventUserSelect } from './CSSMacros';
import { DataAttributes } from './DataAttributes';
import { DataGrid } from './DataGrid';
import { DataGridColors } from './DataGridColors';
import { DefaultFormatter } from './DefaultFormatter';
import { measureWidth } from './Utils';

export class HeaderColumn extends React.PureComponent<HeaderColumn.Props, HeaderColumn.State> {

  public static displayName: string = 'DataGridHeaderColumn';

  public state: HeaderColumn.State = { hovered: false };
  private rootElement: HTMLDivElement | null = null;

  public componentDidMount(): void {
    if (this.props.reportWidthFn) {
      this.props.reportWidthFn(this.props.col.name, measureWidth(this));
    }
  }

  private onStartResize = (event: React.MouseEvent<HTMLDivElement>): void => {
    const resizeMouseX = getClientPosition(event).x;
    const node = this.rootElement!;
    const rect = node.getBoundingClientRect();
    const initialWidth = rect.right - rect.left;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - resizeMouseX;
      this.props.onColumnResize(this.props.col, Math.max(initialWidth + delta, this.props.minWidth));
      e.preventDefault();
    }

    const handleMouseUp = (e: MouseEvent) => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      e.preventDefault();
    }

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    event.preventDefault();
  }

  private columnDivider: JSX.Element = (
    <div
      className='tb-react-dg-hdivider tb-disable-selection'
      onMouseDown={this.onStartResize}
      role='separator'
      style={{
        cursor: 'col-resize',
        bottom: 0,
        position: 'absolute',
        right: -6,
        top: 0,
        width: 12,
        zIndex: 1,
      }}
    />
  );

  private handleClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (this.props.onHeaderClick) {
      this.props.onHeaderClick(null, { colIdx: this.props.colIdx, colKey: this.props.col.dataKey, rowIdx: NaN }, e);
    }
  }

  private handleContextMenu = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (this.props.onHeaderContextMenu) {
      this.props.onHeaderContextMenu(null, { colIdx: this.props.colIdx, colKey: this.props.col.dataKey, rowIdx: NaN }, e);
    }
  }

  private handleDoubleClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (this.props.onHeaderDoubleClick) {
      this.props.onHeaderDoubleClick(null, { colIdx: this.props.colIdx, colKey: this.props.col.dataKey, rowIdx: NaN }, e);
    }
  }

  private handleRef = (element: HTMLDivElement | null): void => {
    this.rootElement = element;
  }

  // ugly helper functions to allow pure rendering
  private setHovered = (): void => {
    this.setState({ hovered: true });
  }

  private setNotHovered = (): void => {
    this.setState({ hovered: false });
  }

  public render(): JSX.Element {
    const col = this.props.col;
    const isSortable = col.sortable !== false;
    const isResizable = col.resizable !== false;

    // Determine column header classes
    let classes = ['tb-react-dg-hcell'];
    if (isSortable) {
      classes.push('tb-react-dg-hcell-sortable');
    }
    if (isResizable) {
      classes.push('tb-react-dg-hcell-resizable');
    }

    let attrs: React.ClassAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement> & DataAttributes = {
      'aria-colindex': this.props.colIdx + 1, // aria-colindex counts from 1
      'aria-rowindex': 1, // aria-rowindex counts from 1
      'aria-selected': this.props.isSelected,
      'aria-sort': this.props.sorted ? (this.props.sortAscending ? 'ascending' : 'descending') : 'none',
      className: classes.join(' '),
      onClick: this.handleClick,
      onContextMenu: this.handleContextMenu,
      onDoubleClick: this.handleDoubleClick,
      onMouseEnter: this.setHovered,
      onMouseLeave: this.setNotHovered,
      role: 'columnheader',
      style: {
        ...PreventUserSelect,
        height: '100%',
      },
    };

    if (col.headerTooltip) {
      attrs.title = col.headerTooltip;
    }
    if (this.props.sorted) {
      attrs['data-tb-test-id'] = this.props.sortAscending ? 'data-grid-hcell-ascending' : 'data-grid-hcell-descending';
    }

    const formatterProps = { ...this.props, ...this.state };

    // tslint:disable:deprecation
    const children = this.props.col.headerComponent ? <this.props.col.headerComponent {...formatterProps} /> :
      this.props.col.headerFormatter?.(formatterProps) ??
      <DefaultFormatter value={col.headerText} testId={col.name + '-HeaderFormatter'} />;
    // tslint:enable:deprecation

    const divWrapperProps = HeaderColumn.getDivWrapperProps(this.props.isSelected, this.props.showVerticalGridLines);
    divWrapperProps.style!.minWidth = this.props.minWidth;
    divWrapperProps.style!.width = this.props.width;

    return (
      <div {...divWrapperProps} ref={this.handleRef}>
        <div {...attrs}>{children}</div>
        {col.resizable === false ? null : this.columnDivider}
      </div>
    );
  }
}

export namespace HeaderColumn {
  export function getDivWrapperProps(isSelected: boolean, showVerticalGridLines?: boolean): React.HTMLAttributes<HTMLDivElement> {
    return {
      className: 'tb-react-dg-hcol',
      style: {
        backgroundColor: isSelected ? DataGridColors.Selected : undefined,
        borderBottom: `1px solid ${isSelected ? DataGridColors.SelectedGridLine : DataGridColors.HeaderBorder}`,
        borderRight: showVerticalGridLines ? `1px solid ${isSelected ? DataGridColors.SelectedGridLine : DataGridColors.VerticalGridLine}` : undefined,
        boxSizing: 'border-box',
        display: 'inline-block',
        height: '100%',
        position: 'relative',
        verticalAlign: 'top', // see https://tableau.slack.com/archives/C8HS5Q5FG/p1596826296321500 or TFSID 1156777
      }
    }
  }

  export interface Props {
    col: DataGrid.Column;
    colIdx: number;
    isSelected: boolean;

    /**
     * Draw vertical grid lines to the right of each column.
     */
    showVerticalGridLines?: boolean;

    sorted: boolean;
    sortAscending: boolean;
    reportWidthFn?: DataGrid.ReportWidthFn;
    width?: number;
    minWidth: number;

    onHeaderClick?: DataGrid.ClickHandler;
    onHeaderDoubleClick?: DataGrid.ClickHandler;
    onHeaderContextMenu?: DataGrid.ClickHandler;

    // tslint:disable-next-line:no-any
    onColumnResize: (column: DataGrid.Column, width: number) => any;
  }

  export interface State {
    hovered: boolean;
  }

  // Header formatters get all props and state of the HeaderColumn
  export type HeaderFormatterProps = Props & State;

}
